/**
 * The client's price funnel: where the price can still land, narrowing phase
 * by phase over real time, against what has been spent, with the schedule's
 * original plan and how far it has moved. Three things make it and nothing
 * else (design study 17, third draft; Jason, 2026-10-07): an estimate a step
 * of the job delivered is the band from its day until the next one (a step
 * that delivers an estimate, `deliversEstimate`: Discovery by default, the
 * Quote, the bid-basis budget); ahead of today, one straight cone to a point
 * at the frame's far end, the confirmed budget's amount for the work, else the
 * middle of today's range, the one figure printed ahead of today and plainly
 * made up (L-11); and the client's budget, once confirmed, caps the top from
 * that day, what runs over it kept beside the point. The lead's range stands in only while no step has
 * delivered an estimate. A mark ahead is dated when someone gave it a date,
 * never from a schedule's defaults (Jason, 2026-10-03: "only show stuff in the
 * funnel time wise that has actually been estimated"); the gate that closes a
 * phase, on the job and dated by nobody, is placed by the shares of the time
 * the tree gives the phases still ahead, and says it is not dated (Jason,
 * 2026-10-07: "ensure that the phase change gates show up on the funnel";
 * "do it as percentages not actual times"). The phases are the tree's, named with what
 * happens in each and where the price stands by its end, each ended by what
 * closes it on the job (else its last step). Pure: the API gathers the facts
 * (core `funnelFacts`), this lays them out on a timeline.
 */

import type { PhaseKey } from './process/phases.ts';

export type FunnelPhaseKey = PhaseKey;

export const PLAN_CAUSES = ['baseline', 'adjustment', 'change_order'] as const;
export type PlanCause = (typeof PLAN_CAUSES)[number];
/** A planned date for a step, with why it was set. */
export interface FunnelPlan {
  on: string;
  cause: PlanCause;
  reason: string | null;
}

/** An estimate a step of the job delivered: the band from its day until the next (design study 17). */
export interface FunnelEstimate {
  on: string;
  lowCents: number;
  highCents: number;
  /** A line on how it was reached, which the client reads too. */
  note: string;
  /** The step that delivered it, by its name as the reader sees it. */
  name: string;
  /** That step's picture, for its marker on the funnel; none for a step with no picture yet. */
  icon?: string | null;
}

/** The client's budget as confirmed: the cap on the band from that day, all in, and what is left for the work. */
export interface FunnelCap {
  on: string;
  totalCents: number;
  workCents: number;
}

/** An estimate as a mark carries it: the latest set on that step. */
export interface MarkEstimate {
  on: string;
  lowCents: number;
  highCents: number;
  note: string;
}

export interface FunnelInput {
  /** YYYY-MM-DD, in NSB's time zone. */
  today: string;
  startedOn: string;
  /** The day it was cancelled; null while it runs, or once it ended otherwise. */
  endedOn: string | null;
  /**
   * The day a job completed or archived ended: its last step's (core `funnelFacts`, `jobFinishedOn`). A cancelled job's
   * `endedOn` stands before it.
   */
  finishedOn?: string | null;
  /** The client's budget band from the lead: the band only while no step has delivered an estimate. */
  budget: { lowCents: number; highCents: number } | null;
  /** The estimates the job's steps delivered, oldest first: each the band from its day until the next; the latest narrowed ahead. */
  estimates?: FunnelEstimate[];
  /** The confirmed budget's cap on the band, from the day it was confirmed. */
  cap?: FunnelCap | null;
  /** In the order the work reaches them. */
  gates: {
    key: string;
    name: string;
    note: string | null;
    passedOn: string | null;
    /** Every planned date ever recorded for the gate, oldest first: what dates it. */
    plans: FunnelPlan[];
    /** A gate locks something until it is passed (a diamond); a milestone or an estimate is a point the schedule hangs on (a ring). Gate when unsaid. */
    mark?: 'gate' | 'ring';
    /** Of the price spread at the start, the share still open once it is passed: as the tree's phases closed by then say. */
    sureAfter?: number;
    /** It delivers an estimate: where a new band comes, and the one set on it so far. */
    deliversEstimate?: boolean;
    estimate?: MarkEstimate | null;
  }[];
  /**
   * The tree's phases, in order, each the job has steps in, with what ends it on the job (the mark that closes it, by
   * its key among `gates`, else its last step) and how sure the price is once it has.
   */
  phases: {
    key: FunnelPhaseKey;
    name: string;
    what: string;
    /** Of the price spread at the start, the share still open once it has ended. */
    sureAfter: number;
    /** Its share of the time, a whole percentage, a weight among the phases still ahead (the tree's); none, no time of its own. */
    share?: number;
    /** Its last step, when nothing on the job closes it: passed once every step of the phase is done. */
    end: { key: string; name: string; passedOn: string | null; plans: FunnelPlan[] };
  }[];
  spent: { on: string; amountCents: number }[];
  /**
   * The frame, given, for a drawing with no today of its own to centre: the master's prototypical job, the whole of it
   * (core `prototypeFunnel`). A job's funnel never gives one.
   */
  frame?: { from: string; to: string };
}

export interface FunnelGateDto {
  key: string;
  name: string;
  /** A gate (drawn as a diamond), or a milestone or an estimate's step (a ring). */
  mark: 'gate' | 'ring';
  note: string | null;
  state: 'passed' | 'next' | 'upcoming';
  /**
   * When it was passed, or when it is planned; for a gate closing a phase that nobody has dated, where the phases'
   * shares place it (`placed`); null when nobody has dated it yet, or once the project has ended.
   */
  on: string | null;
  /** Not dated: placed by its phase's share of the time (a date someone sets always wins). */
  placed?: boolean;
  /** Give or take, in days, for a gate still ahead. */
  giveOrTakeDays: number;
  /** The first plan for this gate, and how far it has moved since (later is positive). */
  originalOn: string | null;
  slipDays: number;
  slipCause: PlanCause | null;
  slipReason: string | null;
  /**
   * Where the price stands once this mark is passed: the estimate in force on its day for the past, expected for the
   * future, the budget's cap held on both; null with no estimate yet.
   */
  range: { lowCents: number; highCents: number } | null;
  /** It delivers an estimate: a new band comes here, and this is the one set on it so far. */
  deliversEstimate: boolean;
  estimate: MarkEstimate | null;
}

/** A phase: what happens in it, when, and the price by its end. */
export interface FunnelPhaseDto {
  key: FunnelPhaseKey;
  name: string;
  what: string;
  /** From the end of the phase before (the project's start, for the first); null when that is not dated. */
  startOn: string | null;
  /** Its gate's day: passed, planned, or placed by the shares (`placed`); null while nobody has dated it. */
  endOn: string | null;
  /** Its end is not dated: its closing gate is placed by its share of the time. */
  placed?: boolean;
  /** What ends it: the step that closes it, else the job's last step in it. */
  endName: string;
  state: 'done' | 'now' | 'ahead';
  /** The price by its end: what it was, for a phase done; expected, for one ahead. */
  range: { lowCents: number; highCents: number } | null;
  /** Of the spread, the share the tree says is still open once it has closed: the confidence at the phase transition. */
  leaves: number;
}

export interface FunnelDto {
  startOn: string;
  endOn: string;
  today: string;
  /** The day it ended, cancelled, completed or archived (L-21): the frame runs from its start to it, with no today in it. */
  endedOn: string | null;
  /**
   * The band, oldest first; projected points lie ahead of today. Where the budget's cap holds the top, `overCents` is
   * what the estimate runs over it by (absent where nothing does).
   */
  band: { on: string; lowCents: number; highCents: number; projected: boolean; overCents?: number }[];
  /** Spent so far, as it accumulated. */
  spent: { on: string; cumulativeCents: number }[];
  gates: FunnelGateDto[];
  /** The tree's phases, in order, each the job has steps in. */
  phases: FunnelPhaseDto[];
  schedule: { plannedEndOn: string | null; projectedEndOn: string | null; slipDays: number };
  /** The estimates the job's steps delivered, oldest first: what the band is made of. */
  estimates: FunnelEstimate[];
  /** The confirmed budget's cap, from its day; null with none confirmed. */
  cap: FunnelCap | null;
  /**
   * The master's prototypical job (L-12, core `prototypeFunnel`): its time the job's, the whole of it, laid out by the
   * phases' shares, and its numbers the price's spread either way round a middle (`prototypeSpread`), never money or
   * dates. A job's funnel is never one.
   */
  prototype?: true;
}

const DAY = 86_400_000;
const toTime = (d: string) => Date.parse(`${d}T00:00:00Z`);
export const daysBetween = (a: string, b: string) => Math.round((toTime(b) - toTime(a)) / DAY);
export const addDays = (d: string, n: number) => new Date(toTime(d) + n * DAY).toISOString().slice(0, 10);
const later = (a: string, b: string) => (a > b ? a : b);

type Range = { lowCents: number; highCents: number };
type Point = FunnelDto['band'][number];

export function buildFunnel(given: FunnelInput): FunnelDto {
  // The marks and the phases' ends come named: a step's title in Title Case, a line written for the client as written
  // (funnelFacts; the Introduction's example names its own).
  const input = given;
  const { today } = input;
  // Ended: cancelled on its day, or completed or archived on its last step's (L-21, option A as Jason chose it, 2026-10-08).
  const ended = input.endedOn ?? input.finishedOn ?? null;
  const horizon = ended ?? today;
  const estimates = [...(input.estimates ?? [])].sort((a, b) => a.on.localeCompare(b.on));
  const cap = input.cap ?? null;

  // The band's corners: each estimate on its own day (the last of a day where two were set on it), the lead's range from
  // the start only while no step has delivered one. Between two the band runs straight from one to the next, never a
  // step (Jason, 2026-10-07: "the funnel always has lines between bands never steps").
  const corners: { on: string; r: Range }[] = estimates.length
    ? estimates.filter((e, i) => e.on <= horizon && estimates[i + 1]?.on !== e.on).map((e) => ({ on: e.on, r: { lowCents: e.lowCents, highCents: e.highCents } }))
    : input.budget
      ? [{ on: input.startedOn, r: { ...input.budget } }]
      : [];
  /** Where the line between two corners stands on a day. */
  const between = (a: { on: string; r: Range }, b: { on: string; r: Range }, on: string): Range => {
    const t = daysBetween(a.on, b.on) ? daysBetween(a.on, on) / daysBetween(a.on, b.on) : 1;
    return { lowCents: Math.round(a.r.lowCents + (b.r.lowCents - a.r.lowCents) * t), highCents: Math.round(a.r.highCents + (b.r.highCents - a.r.highCents) * t) };
  };
  // The band in force on a day: on the line between the corners either side of it, the last one's after it; none
  // before the first.
  const inForce = (on: string): Range | null => {
    const i = corners.findIndex((c) => c.on > on);
    if (i === 0 || !corners.length) return null;
    if (i < 0) return { ...corners.at(-1)!.r };
    return between(corners[i - 1]!, corners[i]!, on);
  };
  // The cap holds the top from the day the budget was confirmed; what runs over it is kept beside the point.
  const raw = (on: string, r: Range, projected = false): Point => ({ on, lowCents: r.lowCents, highCents: r.highCents, projected });
  const point = (on: string, r: Range, projected = false): Point =>
    cap && on >= cap.on && r.highCents > cap.totalCents
      ? { on, lowCents: Math.min(r.lowCents, cap.totalCents), highCents: cap.totalCents, projected, overCents: r.highCents - cap.totalCents }
      : raw(on, r, projected);
  const rangeOf = (p: Point): Range => ({ lowCents: p.lowCents, highCents: p.highCents });
  /** Where the price stood on a day: the band in force, under the cap if it held by then. */
  const rangeOn = (on: string): Range | null => {
    const r = inForce(on);
    return r ? rangeOf(point(on, r)) : null;
  };

  // The band so far: straight from each corner to the next, then the last held to today (or the day it ended). The
  // budget's day is a corner too, as an estimate's is (P-25; Jason, 2026-10-24: "I'm curious why this is a hard step,
  // unless there were two changes very close together I'd expect this to slope between numbers"): the top runs straight
  // from the corner before it to the budget on its day and is held there after, never a step; two days apart still
  // looks steep, as it should. Where the price stood on a day before it is still the estimate (`rangeOn`): the drawing
  // keeps what the estimate runs over the sloping top beside it, hatched (web `overRuns`).
  const band: Point[] = [];
  const path = corners.length && corners.at(-1)!.on < horizon ? [...corners, { on: horizon, r: corners.at(-1)!.r }] : corners;
  path.forEach((c, i) => {
    band.push(point(c.on, c.r));
    const next = path[i + 1];
    if (cap && next && c.on < cap.on && cap.on < next.on) band.push(point(cap.on, between(c, next, cap.on)));
  });
  // The frame, read before what lies ahead so the cone can reach its far end: from twenty days before the first day
  // anything happened to the last mark's give or take past its plan (or sixty days past today), today in the middle; a
  // job that has ended, from just before its start to just after its end.
  const gateDay = (g: FunnelInput['gates'][number]): string | null => g.passedOn ?? (ended || !g.plans.length ? null : later(g.plans.at(-1)!.on, today));
  const giveOrTake = (on: string) => Math.max(7, Math.round(daysBetween(today, on) * 0.15));
  const lastDated = [...input.gates].reverse().find((g) => gateDay(g) !== null);
  const known = [input.startedOn, ...band.map((pt) => pt.on), ...input.spent.map((x) => x.on), ...input.gates.flatMap((g) => (gateDay(g) ? [gateDay(g)!] : []))];
  const first = known.reduce((a, b) => (a < b ? a : b));
  // Ended (L-21, option A as Jason chose it, 2026-10-08: "an ended job is drawn from its start to its end"): from just
  // before its start to just after its end, its whole story filling the drawing, with no today in it, and the same in
  // five years; the room past its end keeps ENDED clear of the drawing's edge however long it ran.
  const endedLast = ended ? known.reduce(later, ended) : null;
  const endedSpan = endedLast ? daysBetween(first, endedLast) : 0;
  const lastOn = endedLast
    ? addDays(endedLast, Math.max(45, Math.round(endedSpan * 0.05)))
    : addDays(known.reduce(later, today), lastDated ? (lastDated.passedOn ? 0 : giveOrTake(gateDay(lastDated)!)) + 30 : 60);
  const firstOn = addDays(first, -(endedLast ? Math.max(20, Math.round(endedSpan * 0.02)) : 20));
  // Running, today in the middle, always (Jason, 2026-10-07: "'today' should always show centered on the funnel with the
  // scale and images change as required to show the full scope"): as far each side as the farther of the start and the
  // end reach, so the whole job is drawn and today sits at the centre. The seasons follow this frame.
  const half = Math.max(daysBetween(firstOn, horizon), daysBetween(horizon, lastOn));
  // A frame given (the master's prototypical job, which has no today to centre) is drawn as given.
  const frameStart = input.frame?.from ?? (ended ? firstOn : addDays(horizon, -half));
  const frameEnd = input.frame?.to ?? (ended ? lastOn : addDays(horizon, half));

  // What lies ahead (Jason, 2026-10-07: "if a budget is specified, the funnel should go with dashed lines and a lighter
  // grey into the future all the way to the right meeting at the budget line. if a budget is not specified, the lines
  // meet at the far end in the middle of the last recorded high low range"): straight from where the price stands today
  // to one point at the frame's far end, the confirmed budget's amount for the work (Jason, 2026-10-07, L-8: "with a
  // budget, the funnel should narrow in on the budget line, not the all in": the reserve held inside the budget is not
  // the price), else the middle of today's range. A mark or a phase ahead reads where it stands off that cone, on its
  // date; one nobody has dated has no place on it.
  const nowRange = ended ? null : inForce(horizon);
  const target = nowRange ? (cap ? cap.workCents : Math.round((nowRange.lowCents + nowRange.highCents) / 2)) : null;
  const coneAt = (on: string | null): FunnelGateDto['range'] =>
    nowRange && target !== null && on ? rangeOf(point(on, between({ on: horizon, r: nowRange }, { on: frameEnd, r: { lowCents: target, highCents: target } }, on), true)) : null;
  if (nowRange && target !== null && frameEnd > horizon) band.push(point(frameEnd, { lowCents: target, highCents: target }, true));

  // The gate that closes a phase, on the job and dated by nobody, placed by the phases' shares of the time (Jason,
  // 2026-10-07: "On each transition, of the time left"; "so the funnel looks right before anything is dated"): from
  // today, or the last phase end dated or passed before it, to the next one dated after it, else the frame's far end,
  // the time between shared by the phases between, each by its share. It never moves the frame, and a date someone sets
  // always wins. A phase ending at a step that is no gate is placed the same way, its end alone and never a mark, so
  // every phase has its place in order (L-37: Discovery ending at a step of work had no end, Design no start).
  const placedOn = new Map<string, string>();
  const endPlacedOn = new Map<string, string>();
  if (!ended) {
    const byKey = new Map(input.gates.map((g) => [g.key, g]));
    const ends = input.phases.map((p) => {
      const g = byKey.get(p.end.key);
      const day = g ? gateDay(g) : (p.end.passedOn ?? (p.end.plans.length ? later(p.end.plans.at(-1)!.on, today) : null));
      return { key: p.end.key, share: p.share ?? 0, day, gate: g !== undefined && (g.mark ?? 'gate') === 'gate' };
    });
    let from = today;
    for (let i = 0; i < ends.length; ) {
      const dated = ends[i]!.day;
      if (dated !== null) {
        from = later(from, dated);
        i++;
        continue;
      }
      let j = i;
      while (j < ends.length && ends[j]!.day === null) j++;
      const to = j < ends.length ? later(ends[j]!.day!, from) : later(frameEnd, from);
      const shares = ends.slice(i, Math.min(j + 1, ends.length)).reduce((s, e) => s + e.share, 0);
      let along = 0;
      for (const e of ends.slice(i, j)) {
        along += e.share;
        if (shares <= 0) continue;
        const day = addDays(from, Math.round((daysBetween(from, to) * along) / shares));
        endPlacedOn.set(e.key, day);
        if (e.gate) placedOn.set(e.key, day);
      }
      i = j;
    }
  }

  // Marks: passed on their day; ahead, at their latest plan, or placed by the shares; one nobody has dated stays off the
  // calendar.
  const gates: FunnelGateDto[] = [];
  let nextFound = false;
  for (const g of input.gates) {
    const original = g.plans[0] ?? null;
    const latest = g.plans.at(-1) ?? null;
    const delivered = { deliversEstimate: g.deliversEstimate ?? false, estimate: g.estimate ?? null };
    if (g.passedOn) {
      gates.push({
        key: g.key,
        name: g.name,
        mark: g.mark ?? 'gate',
        note: g.note,
        state: 'passed',
        on: g.passedOn,
        giveOrTakeDays: 0,
        originalOn: original?.on ?? null,
        slipDays: original ? Math.max(0, daysBetween(original.on, g.passedOn)) : 0,
        slipCause: original && g.passedOn > original.on ? (latest?.cause ?? 'adjustment') : null,
        slipReason: original && g.passedOn > original.on ? (latest?.reason ?? null) : null,
        range: rangeOn(g.passedOn),
        ...delivered,
      });
      continue;
    }
    const state = nextFound ? 'upcoming' : 'next';
    nextFound = true;
    if (ended) {
      gates.push({ key: g.key, name: g.name, mark: g.mark ?? 'gate', note: g.note, state, on: null, giveOrTakeDays: 0, originalOn: original?.on ?? null, slipDays: 0, slipCause: null, slipReason: null, range: null, ...delivered });
      continue;
    }
    const placed = !latest ? placedOn.get(g.key) : undefined;
    if (placed) {
      // Not dated: placed by its phase's share, where the cone stands on that day.
      gates.push({ key: g.key, name: g.name, mark: g.mark ?? 'gate', note: g.note, state, on: placed, placed: true, giveOrTakeDays: 0, originalOn: null, slipDays: 0, slipCause: null, slipReason: null, range: coneAt(placed), ...delivered });
      continue;
    }
    if (!latest) {
      // Not dated by anyone: no place on the cone, so no price expected by it.
      gates.push({ key: g.key, name: g.name, mark: g.mark ?? 'gate', note: g.note, state, on: null, giveOrTakeDays: 0, originalOn: null, slipDays: 0, slipCause: null, slipReason: null, range: null, ...delivered });
      continue;
    }
    const on = later(latest.on, today);
    const slip = original ? daysBetween(original.on, on) : 0;
    gates.push({
      key: g.key,
      name: g.name,
      mark: g.mark ?? 'gate',
      note: g.note,
      state,
      on,
      giveOrTakeDays: giveOrTake(on),
      originalOn: original?.on ?? null,
      slipDays: Math.max(0, slip),
      slipCause: slip > 0 ? (latest?.cause ?? 'adjustment') : null,
      slipReason: slip > 0 ? (latest?.reason ?? null) : null,
      range: coneAt(on),
      ...delivered,
    });
  }
  band.sort((a, b) => a.on.localeCompare(b.on) || Number(a.projected) - Number(b.projected));

  let total = 0;
  const spent = [...input.spent]
    .sort((a, b) => a.on.localeCompare(b.on))
    .map((s) => ({ on: s.on, cumulativeCents: (total += s.amountCents) }));

  // The phases: each from the end of the one before to its own end, done, under way now, or ahead.
  const phases: FunnelPhaseDto[] = [];
  let from: string | null = input.startedOn;
  let before: FunnelPhaseDto['state'] = 'done';
  const stateOf = (passed: boolean): FunnelPhaseDto['state'] => (passed ? 'done' : before === 'done' && !ended ? 'now' : 'ahead');
  // Each ended by the mark that closes it, else by its last step, where the price stands by then read off the cone.
  const marks = new Map(input.gates.map((g, i) => [g.key, gates[i]!]));
  for (const p of input.phases) {
    const g = marks.get(p.end.key);
    const passedOn = g ? (g.state === 'passed' ? g.on : null) : p.end.passedOn;
    const latest = p.end.plans.at(-1) ?? null;
    const dated = g ? g.on : (passedOn ?? (latest && !ended ? later(latest.on, today) : null));
    // Nobody dated its end and it is no gate: placed by its share, where the cone stands on that day.
    const shared = dated === null ? (endPlacedOn.get(p.end.key) ?? null) : null;
    const endOn = dated ?? shared;
    const range = shared ? coneAt(shared) : g ? g.range : passedOn ? rangeOn(passedOn) : ended ? null : coneAt(endOn);
    const state = stateOf(passedOn !== null);
    // A placed end is not dated: the phase follows its gate's place, or its own share's, and says so.
    phases.push({ key: p.key, name: p.name, what: p.what, startOn: from, endOn, ...(g?.placed || shared ? { placed: true } : {}), endName: g?.name ?? p.end.name, state, range, leaves: p.sureAfter });
    before = state;
    from = endOn;
  }

  // The schedule is what someone planned: a last mark placed by the shares projects no end.
  const finalGate = gates.at(-1) ?? null;
  const finalOn = finalGate?.placed ? null : (finalGate?.on ?? null);
  return {
    startOn: frameStart,
    endOn: frameEnd,
    today,
    endedOn: ended,
    band,
    spent,
    gates,
    phases,
    schedule: {
      plannedEndOn: finalGate?.originalOn ?? null,
      projectedEndOn: finalOn,
      slipDays: finalGate?.originalOn && finalOn ? Math.max(0, daysBetween(finalGate.originalOn, finalOn)) : 0,
    },
    estimates,
    cap,
  };
}

